import { X, Clock, Car, ShoppingBag, Undo2 } from "lucide-react";

const NEXT_LABEL = {
  Received: "Start Preparing",
  Preparing: "Mark Ready",
  Ready: "Mark Picked Up",
};
const NEXT_STATUS = {
  Received: "Preparing",
  Preparing: "Ready",
  Ready: "Completed",
};
const BTN_TINT = {
  Received: "bg-amber-500 hover:bg-amber-600",
  Preparing: "bg-emerald-600 hover:bg-emerald-700",
  Ready: "bg-stone-800 hover:bg-stone-900",
};
const STATUS_PILL = {
  Received: "bg-stone-200 text-stone-700",
  Preparing: "bg-amber-100 text-amber-800",
  Ready: "bg-emerald-100 text-emerald-800",
};

function elapsed(iso) {
  const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * OnlineOrdersPanel — lets the cashier see customer online orders from the POS
 * and advance them Received → Preparing → Ready → Picked up, exactly like the KDS.
 * Pure presentational: parent owns the polled `orders` list + the `onAdvance` call.
 */
export function OnlineOrdersPanel({ orders, busyId, onAdvance, onCurbsideServed, pickedUp = [], onUndoPickup, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4"
      data-testid="pos-online-orders-panel"
    >
      <div className="bg-[#FDFBF7] w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden">
        <div className="chalkboard-band px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-brand-yellow" />
            <span className="font-display text-brand-yellow text-xl leading-none">Online Orders</span>
            <span
              data-testid="pos-online-panel-count"
              className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest"
            >
              {orders.length} open
            </span>
          </div>
          <button
            onClick={onClose}
            data-testid="pos-online-close-btn"
            className="text-brand-yellow hover:text-yellow-200 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-3 space-y-3">
          {orders.length === 0 ? (
            <div className="py-16 text-center text-stone-400" data-testid="pos-online-empty">
              <div className="font-hand text-4xl text-stone-500 mb-1">All caught up!</div>
              <div className="text-xs uppercase tracking-widest">No open online orders</div>
            </div>
          ) : (
            orders.map((o) => (
              <div
                key={o.id}
                data-testid={`pos-online-card-${o.order_number}`}
                className="bg-white border border-stone-200 rounded-xl p-3 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-brand-red text-2xl leading-none">{o.order_number}</div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mt-1">
                      {o.customer_name}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      data-testid={`pos-online-status-${o.order_number}`}
                      className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${STATUS_PILL[o.status] || "bg-stone-200 text-stone-700"}`}
                    >
                      {o.status}
                    </span>
                    <span className="text-xs font-mono font-black text-stone-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {elapsed(o.created_at)}
                    </span>
                  </div>
                </div>

                {o.curbside && (
                  <div
                    data-testid={`pos-online-curbside-${o.order_number}`}
                    className={`mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${
                      o.curbside.delivered_at
                        ? "bg-emerald-100 text-emerald-800"
                        : o.curbside.checked_in_at
                        ? "bg-brand-yellow text-stone-900 animate-pulse"
                        : "bg-stone-200 text-stone-700"
                    }`}
                  >
                    <Car className="w-3 h-3" />
                    {o.curbside.delivered_at
                      ? "Brought out"
                      : o.curbside.checked_in_at
                      ? "Curbside — here now"
                      : "Curbside — not arrived"}
                    {o.curbside.vehicle ? ` · ${o.curbside.vehicle}` : ""}
                    {o.curbside.spot ? ` · ${o.curbside.spot}` : ""}
                  </div>
                )}

                {o.curbside && !o.curbside.delivered_at && (
                  <button
                    onClick={() => onCurbsideServed?.(o)}
                    disabled={busyId === o.id}
                    data-testid={`pos-online-curbside-serve-${o.order_number}`}
                    className={`mt-2 w-full flex items-center justify-center gap-2 text-white font-black uppercase tracking-widest text-xs px-4 py-3 rounded-lg active:scale-[0.98] transition-transform disabled:opacity-50 ${
                      o.curbside.checked_in_at ? "bg-sky-600 hover:bg-sky-700 animate-pulse" : "bg-sky-600/70 hover:bg-sky-700"
                    }`}
                  >
                    <Car className="w-4 h-4" />
                    {busyId === o.id ? "…" : "Bring Out to Curbside"}
                  </button>
                )}
                {o.curbside?.delivered_at && (
                  <div
                    data-testid={`pos-online-curbside-done-${o.order_number}`}
                    className="mt-2 w-full flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 font-black uppercase tracking-widest text-xs px-4 py-2.5 rounded-lg"
                  >
                    <Car className="w-4 h-4" />
                    Brought out
                  </div>
                )}

                <ul className="text-sm space-y-1 border-t border-stone-100 mt-2 pt-2">
                  {(o.items || []).map((it, idx) => (
                    <li key={`${it.item_id}-${idx}`}>
                      <span className="font-black text-brand-red mr-1.5">{it.quantity}×</span>
                      <span className="font-bold text-stone-800">{it.name}</span>
                      {it.modifications && (
                        <div className="text-xs italic text-brand-red font-bold ml-6">⚑ {it.modifications}</div>
                      )}
                    </li>
                  ))}
                </ul>

                {o.notes && (
                  <div className="text-xs bg-brand-yellow/40 border border-brand-yellow rounded p-2 mt-2 text-stone-900">
                    <span className="font-black uppercase tracking-widest mr-1">Note:</span>
                    {o.notes}
                  </div>
                )}

                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">
                    Pickup: <span className="text-stone-800">{o.pickup_time || "ASAP"}</span>
                  </div>
                  <button
                    onClick={() => onAdvance(o)}
                    disabled={busyId === o.id}
                    data-testid={`pos-online-advance-${o.order_number}`}
                    className={`text-white font-black uppercase tracking-widest text-xs px-4 py-3 rounded-lg active:scale-[0.98] transition-transform disabled:opacity-50 ${
                      BTN_TINT[o.status] || "bg-stone-700"
                    }`}
                  >
                    {busyId === o.id ? "…" : NEXT_LABEL[o.status] || "Advance"}
                  </button>
                </div>
              </div>
            ))
          )}

          {pickedUp.length > 0 && (
            <div className="pt-2 mt-1 border-t border-stone-200" data-testid="pos-online-pickedup-section">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-black mb-2 flex items-center gap-1">
                <Undo2 className="w-3 h-3" />
                Recently picked up — tap to pull back
              </div>
              <div className="flex flex-wrap gap-2">
                {pickedUp.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => onUndoPickup?.(o)}
                    data-testid={`pos-online-undo-${o.order_number}`}
                    className="flex items-center gap-1.5 border border-stone-300 hover:border-brand-red hover:bg-brand-red/5 rounded-lg px-2.5 py-1.5 text-left"
                  >
                    <Undo2 className="w-3.5 h-3.5 text-brand-red shrink-0" />
                    <span>
                      <span className="font-display text-brand-red text-base leading-none">{o.order_number}</span>
                      <span className="block text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                        {o.customer_name}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const ONLINE_ACTIVE = ["Received", "Preparing", "Ready"];
export { NEXT_STATUS as ONLINE_NEXT_STATUS };
